<template>
  <div class="page-content">
    
    <!-- Tabs Card with Add Performer Button -->
    <v-card class="mx-auto elevation-2 tabs-card mt-5">
      <v-container fluid>
        <v-row align="center" justify="space-between">
          <v-tabs v-model="tab" class="dashboard-tab">
            <v-tab v-for="tabItem in tabItems" :key="tabItem.value" :value="tabItem.value">
              {{ tabItem.label }}
            </v-tab>
          </v-tabs>
          <v-btn class="btn-secondary mr-3" @click="openAddDialog">Add Performer</v-btn>
        </v-row>
      </v-container>
    </v-card>
  
    <!-- Divider Bar -->
    <div style="display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; height: 48px; background: linear-gradient(180deg, #1c2321 0%, #3A4542 100%); color: white; border-radius: 6px; margin-top: 28px;">
      <h2 style="font-size: 18px; font-weight: 600; color: white;">Manage Models</h2>
        <div style="display: flex; align-items: center; gap: 8px;">
        <div style="display: flex; align-items: center; gap: 8px;">
          <v-text-field
            v-if="showSearch"
            v-model="searchQuery"
            hide-details
            density="dense"
            single-line
            flat
            placeholder="Search by name"
            style="max-width: 220px; min-width: 220px; background-color: transparent; color: white; border-bottom: 1px solid white;"
            class="white--text"
            @blur="showSearch = false"
          ></v-text-field>
          <v-icon @click="showSearch = !showSearch" style="color: white; cursor: pointer;">mdi-magnify</v-icon>
        </div>
        <v-menu offset-y>
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
                style="text-transform: none; font-size: 1rem; background-color: transparent; color: white;"
            >
              <span style="font-weight: 400; margin-right: 4px;">Sort by:</span>
              {{ sortOption }}
              <v-icon small style="color: white;">mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item
              density="compact"
              v-for="option in sortOptions"
              :key="option"
              @click="sortModels(option)"
            >
              <v-list-item-title>{{ option }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <!-- Studio and Active Tabs Grouped Table with Section Divider -->
    <v-data-table
      v-if="['studio', 'active', 'morning', 'evening', 'night'].includes(tab)"
      :headers="headers"
      :items="groupedModelsFiltered"
      class="elevation-1 mt-5 bold-header-table"
      :items-per-page="-1"
      disable-sort
      hide-default-footer
    >
      <!-- Custom Header -->
      <template v-slot:column.header="{ header }">
        <th>{{ header.title }}</th>
      </template>

      <!-- Render Rows -->
      <template v-slot:item="{ item, index }">
        <tr :class="{ 'bold-border': item.addBorder }">
          <td>{{ item.fullName }}</td>
          <td>{{ item.stageName }}</td>
          <td>{{ item.location }}</td>
          <td>{{ item.status }}</td>
          <td>{{ item.renta === 1 ? 'Yes' : 'No' }}</td>
          <td>{{ item.startDate }}</td>
          <td>{{ item.docNumber }}</td>          
          <td>
            <v-btn icon class="btn-primary" density="comfortable" style="margin-right: 4px;" @click="viewModel(item)">
              <v-icon>mdi-account-details</v-icon>
            </v-btn>
            <v-btn icon class="btn-primary" density="comfortable" style="margin-right: 4px;" @click="editModel(item)">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </td>
        </tr>
      </template>
    </v-data-table>

    <!-- Data Table for Other Tabs -->
    <v-data-table
      v-else
      :headers="headers"
      :items="filteredModels"
      :items-per-page="-1"
      class="elevation-1 mt-5"
    >
      <!-- Custom Header -->
      <template v-slot:column.header="{ header }">
        <th>{{ header.text }}</th>
      </template>

      <!-- Render Rows with Border -->
      <template v-slot:item="{ item, index }">
        <tr :class="{ 'bold-border': item.addBorder }">
          <td>{{ item.fullName }}</td>
          <td>{{ item.stageName }}</td>
          <td>{{ item.location }}</td>
          <td>{{ item.status }}</td>
          <td>{{ item.renta === 1 ? 'Yes' : 'No' }}</td>
          <td>{{ item.startDate }}</td>
          <td>{{ item.docNumber }}</td>          
          <td>
            <v-btn icon class="btn-primary" density="comfortable" style="margin-right: 4px;" @click="viewModel(item)">
              <v-icon>mdi-account-details</v-icon>
            </v-btn>
            <v-btn icon class="btn-primary" density="comfortable" style="margin-right: 4px;" @click="editModel(item)">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </td>
        </tr>
      </template>
    </v-data-table>

    <!-- Dialog for Adding/Editing Performer -->
    <v-dialog v-model="dialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ isEditing ? 'Edit Performer' : 'Add New Performer' }}</span>
        </v-card-title>

        <v-card-text>
          <v-form>
            <v-text-field v-model="newModel.fullName" label="Full Name" outlined></v-text-field>
            <v-text-field v-model="newModel.stageName" label="Stage Name" outlined></v-text-field>
            <v-select
              v-model="newModel.location"
              :items="locationOptions"
              label="Location"
              outlined
            ></v-select>
            <v-select
              v-model="newModel.status"
              :items="['Active', 'Inactive']"
              label="Status"
              outlined
            ></v-select>
            <v-select
              v-model="newModel.renta"
              :items="rentaOptions"
              label="Renta"
              outlined
            ></v-select>
            <v-text-field v-model="newModel.startDate" label="Start Date" type="date" outlined></v-text-field>
            <v-row>
              <v-col cols="5">
                <v-select
                  v-model="newModel.docType"
                  :items="['Cedula', 'Passport', 'PPT', 'Other']"
                  label="Document Type"
                  outlined
                ></v-select>
              </v-col>
              <v-col cols="7">
                <v-text-field
                  v-model="newModel.docNumber"
                  label="Document Number"
                  outlined
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" @click="isEditing ? saveModel() : addModel()">{{ isEditing ? 'Save Changes' : 'Add Performer' }}</v-btn>
          <v-btn v-if="isEditing && newModel.status === 'Inactive' && models.find(m => m.id === currentModelId)?.status === 'Inactive'" icon color="red" @click="hideModel" style="position: absolute; bottom: 6px; left: 8px;">
            <v-icon>mdi-eye-off</v-icon>
          </v-btn>
          <v-btn color="red" @click="dialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { toast } from 'vue3-toastify';
import secureAxios from '../utils/secureAxios'; // Use secureAxios instead of axios
import { format, parseISO, isValid } from 'date-fns';

export default {
  data() {
    return {
      tab: 'studio',
      tabItems: [
        { value: 'studio', label: 'Studio' },
        { value: 'morning', label: 'Morning' },
        { value: 'evening', label: 'Evening' },
        { value: 'night', label: 'Night' },
        { value: 'satellite', label: 'Satellite' },
        { value: 'lata', label: 'Lata' },
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
      ],
      dialog: false,
      isEditing: false,
      newModel: {
        fullName: '',
        stageName: '',
        location: '',
        status: 'Active',
        renta: '',
        startDate: new Date().toISOString().split('T')[0],
        docType: 'Cedula',
        docNumber: ''
      },
      locationOptions: ['Morning', 'Evening', 'Night', 'Satellite', 'Lata'],
      rentaOptions: ['Yes', 'No'],
      models: [],
      headers: [
        { title: 'Full Name', key: 'fullName' },
        { title: 'Stage Name', key: 'stageName' },
        { title: 'Location', key: 'location' },
        { title: 'Status', key: 'status' },
        { title: 'Renta', key: 'renta' },
        { title: 'Start Date', key: 'startDate' },
        { title: 'Document #', key: 'docNumber' },        
        { title: 'Actions', key: 'actions', sortable: false }
      ],
      currentModelId: null,
      searchQuery: '',
      showSearch: false,
      sortOption: 'Name A → Z',
      sortOptions: ['Name A → Z', 'Name Z → A', 'Stage Name A → Z', 'Stage Name Z → A']
    };
  },
  computed: {
    filteredModels() {
      const tab = this.tab;
      let models = this.models;

      // Filter based on the selected tab
      if (['satellite', 'lata', 'inactive'].includes(tab)) {
        // Satellite, Lata: Show models from the specific location
        // Inactive: Show only inactive models
        models = models.filter(model => 
          tab === 'inactive' 
            ? model.status === 'Inactive' 
            : model.location === tab.charAt(0).toUpperCase() + tab.slice(1)
        );
      } else if (['morning', 'evening', 'night'].includes(tab)) {
        // Morning, Evening, Night: Show only active models for the given location
        models = models.filter(model => 
          model.location.toLowerCase() === tab && model.status === 'Active'
        );
      } else if (['studio', 'active'].includes(tab)) {
        // Studio: Show all models from Morning, Evening, Night, Satellite, Lata
        // Active: Show only active models from these locations
        models = models.filter(model => 
          ['Morning', 'Evening', 'Night', 'Satellite', 'Lata'].includes(model.location) &&
          (tab !== 'active' || model.status === 'Active')
        );
      }

      // Apply search query filtering if there is one
      if (this.searchQuery) {
        models = models.filter(model => 
          model.fullName.toLowerCase().includes(this.searchQuery.toLowerCase()) || 
          model.stageName.toLowerCase().includes(this.searchQuery.toLowerCase())
        );
      }

      return models;
    },

    groupedModelsFiltered() {
      // Apply search filtering to grouped models
      return this.groupedModels.filter(model => 
        model.fullName.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        model.stageName.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    },

    groupedModels() {
      if (['studio', 'active'].includes(this.tab)) {
        // Group models by location and add border to the first model in each group
        const morningModels = this.models.filter((model) => model.location === 'Morning' && model.status === 'Active');
        const eveningModels = this.models.filter((model) => model.location === 'Evening' && model.status === 'Active');
        const nightModels = this.models.filter((model) => model.location === 'Night' && model.status === 'Active');
        const satelliteModels = this.models.filter((model) => model.location === 'Satellite' && model.status === 'Active');
        const lataModels = this.models.filter((model) => model.location === 'Lata' && model.status === 'Active');

        const taggedModels = [
          ...morningModels.map((model, index) => ({ ...model, addBorder: index === 0 })),
          ...eveningModels.map((model, index) => ({ ...model, addBorder: index === 0 })),
          ...nightModels.map((model, index) => ({ ...model, addBorder: index === 0 })),
          ...(this.tab === 'active' ? [
            ...satelliteModels.map((model, index) => ({ ...model, addBorder: index === 0 })),
            ...lataModels.map((model, index) => ({ ...model, addBorder: index === 0 }))
          ] : [])
        ];

        return taggedModels;
      }

      // For Satellite, Lata, and Inactive tabs, return filtered models with bold borders
      if (['satellite', 'lata', 'inactive'].includes(this.tab)) {
        return this.filteredModels.map((model, index) => ({
          ...model,
          addBorder: index === 0
        }));
      }

      // Default case, return filtered models
      return this.filteredModels;
    }
  },
  mounted() {
    console.log('Headers:', this.headers);
    this.fetchModels();
  },
  methods: {
    async fetchModels() {
      try {
        console.log('Fetching models...');
        const response = await secureAxios.get('/api/models');
        console.log('Models response:', response.data);
        this.models = response.data.map(model => ({
          ...model,
          startDate: model.startDate && isValid(parseISO(model.startDate)) ? format(parseISO(model.startDate), 'yyyy-MM-dd') : ''
        }));
        console.log('Processed models:', this.models);
      } catch (error) {
        console.error('Error fetching models:', error);
        if (error.response) {
          console.log('Error response status:', error.response.status);
          console.log('Error response data:', error.response.data);
        } else if (error.request) {
          console.log('No response received:', error.request);
        } else {
          console.log('Error setting up request:', error.message);
        }
        this.models = [];
        console.log('Models set to empty array due to error');
      }
    },
    openAddDialog() {
      this.dialog = true;
      this.isEditing = false;
      this.resetForm();
    },
    async addModel() {
      if (!this.newModel.fullName || !this.newModel.stageName || !this.newModel.location || !this.newModel.status) {
        alert("Please fill in all required fields.");
        return;
      }

      const rentaValue = this.newModel.renta === 'Yes' ? 1 : 0;
      const newModelData = {
        ...this.newModel,
        renta: rentaValue,
        startDate: isValid(parseISO(this.newModel.startDate)) ? parseISO(this.newModel.startDate).toISOString().split('T')[0] : null
      };

      try {
        console.log('Adding new model:', newModelData);
        const response = await secureAxios.post('/api/models', newModelData);
        console.log('Add model response:', response.data);
        this.models.push(response.data);
        this.dialog = false;
        toast.success(`Model ${response.data.stageName} added successfully!`);
        this.resetForm();
      } catch (error) {
        console.error('Error adding model:', error);
        if (error.response) {
          console.log('Error response status:', error.response.status);
          console.log('Error response data:', error.response.data);
        }
      }
    },
    resetForm() {
      this.newModel = {
        fullName: '',
        stageName: '',
        location: '',
        status: 'Active',
        renta: '',
        startDate: new Date().toISOString().split('T')[0],
        docType: 'Cedula',
        docNumber: ''
      };
    },
    editModel(model) {
      try {
        const startDateFormatted = model.startDate ? format(parseISO(model.startDate), "yyyy-MM-dd") : '';

        this.dialog = true;
        this.isEditing = true;
        this.newModel = {
          ...model,
          startDate: startDateFormatted,
          renta: model.renta === 1 ? 'Yes' : 'No', // Convert for the select field
          docType: model.docType || 'Cedula',
          docNumber: model.docNumber || ''
        };
        this.currentModelId = model.id;
      } catch (error) {
        console.error("Error formatting date in editModel:", error);
      }
    },
    viewModel(model) {
      console.log('View model:', model);
      alert(`Viewing details of ${model.fullName}`);
    },
    async saveModel() {
      if (!this.isEditing) {
        console.warn('Unexpected behavior: saveModel called when not in editing mode.');
        return;
      }

      const updatedModelData = {
        ...this.newModel,
        renta: this.newModel.renta === 'Yes' ? 1 : 0
      };

      try {
        console.log('Updating model:', updatedModelData);
        const response = await secureAxios.put(`/api/models/${this.currentModelId}`, updatedModelData);
        console.log('Update model response:', response.data);
        const index = this.models.findIndex(model => model.id === this.currentModelId);
        if (index !== -1) {
          this.models.splice(index, 1, response.data);
        }
        this.dialog = false;
        toast.success(`Changes saved for ${response.data.stageName}`);
        this.currentModelId = null;
        this.isEditing = false;
      } catch (error) {
        console.error('Error updating model:', error);
        if (error.response) {
          console.log('Error response status:', error.response.status);
          console.log('Error response data:', error.response.data);
        }
      }
    },
    
    async hideModel() {
  if (!this.currentModelId) return;
  if (!confirm('Are you sure you want to hide this model from the frontend?')) return;

  try {
    await secureAxios.patch(`/api/models/${this.currentModelId}/hidden`, { hidden: 1 });
    this.models = this.models.filter(m => m.id !== this.currentModelId);
    this.dialog = false;
    this.currentModelId = null;
    this.isEditing = false;
  } catch (error) {
    console.error('Error hiding model:', error);
    if (error.response) {
      console.log('Error response status:', error.response.status);
      console.log('Error response data:', error.response.data);
    }
  }
},
    sortModels(option) {
      this.sortOption = option;
      if (option === 'Name A → Z') {
        this.models.sort((a, b) => a.fullName.localeCompare(b.fullName));
      } else if (option === 'Name Z → A') {
        this.models.sort((a, b) => b.fullName.localeCompare(a.fullName));
      } else if (option === 'Stage Name A → Z') {
        this.models.sort((a, b) => a.stageName.localeCompare(b.stageName));
      } else if (option === 'Stage Name Z → A') {
        this.models.sort((a, b) => b.stageName.localeCompare(a.stageName));
      }
    }
  }
};
</script>

<style scoped>
.dashboard-tab .v-tab {
  background-color: white;
  color: #2C74B3;
}

.custom-border-radius {
  border-radius: 20px;
}

.custom-search-bar {
  max-width: 170px;
}

.bold-border td {
  border-top: 0.5px solid black !important; /* Add bold line for visual separation */
}
</style>